<template>
  <div>
    <!-- Botão flutuante -->
    <v-btn
      fab
      color="primary"
      dark
      fixed
      bottom
      right
      class="ma-4"
      @click="isChatOpen = true"
    >
      <v-icon>mdi-chat-outline</v-icon>
    </v-btn>

    <v-navigation-drawer
      v-model="isChatOpen"
      right
      temporary
      width="400"
      app
    >
      <v-toolbar flat color="primary" dark>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          <v-icon>mdi-chat-outline</v-icon> Assistente Sysclass
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="isChatOpen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="chat-content" ref="chatContainer">
        <div
          v-for="(msg, index) in messages"
          :key="index"
          :class="['chat-row', msg.from]"
        >
          <v-avatar size="20" class="avatar">
            <v-icon small color="indigo accent-2">
              {{ msg.from === 'bot' ? 'mdi-robot' : 'mdi-account-outline' }}
            </v-icon>
          </v-avatar>

          <div class="chat-message-wrapper">
            <div class="message-bubble text-body-2 font-weight-regular" v-html="renderMarkdown(msg.text)"></div>
            

            <!-- Botões de feedback (somente para mensagens do bot) -->
            <div v-if="msg.from === 'bot' && index !== 0" class="feedback-buttons mt-1">
              <ButtonsReactionIcon 
                @clicked="sendFeedback(index, 'like')" 
                :color="msg.feedback === 'like' ? 'green' : 'grey'" 
              />
              <ButtonsReactionIcon 
                @clicked="sendFeedback(index, 'dislike')" 
                icon="mdi-thumb-down" 
                :color="msg.feedback === 'dislike' ? 'red' : 'grey'" 
                tooltip="A resposta não foi satisfatória"
                />
            </div>

            <div class="timestamp">{{ msg.timestamp }}</div>
          </div>
        </div>

        <div class="text-center">
          <span v-if="isLoading" class="text-caption">
            <v-icon small>mdi-spin mdi-loading</v-icon> Aguarde...
          </span>
        </div>
      </v-card-text>


      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-textarea
          v-model="newMessage"
          outlined
          auto-grow
          rows="4"
          placeholder="Digite sua mensagem..."
          class="flex-grow-1"
          hide-details
          style="background-color: white;"
          @keyup.enter.exact.prevent="sendMessage"
        ></v-textarea>

        <v-btn
          color="primary"
          class="ml-2"
          @click="sendMessage"
          :disabled="newMessage.trim() === ''"
          elevation="1"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </v-card-actions>
    </v-navigation-drawer>
  </div>
</template>

<script setup>

import { fetchEventSource } from '@microsoft/fetch-event-source'
import { renderMarkdown } from '@/composables/useMarkdown'
import { useStore } from '@/composables/useStore'

import { ref } from 'vue'

const store = useStore()
const ActionRequest = (action, params) => store.dispatch(action, params)

const isChatOpen = ref(false)
const newMessage = ref('')
const isLoading = ref(false)
const messages = ref([
  {
    from: 'bot',
    text: 'Olá, como posso ajudar?',
    question: null,
    timestamp: null
  }
])

const sendFeedback = (index, type) =>  {

    messages.value[index].feedback = type;
    let msg = messages.value[index];

    // Envia para o Google Analytics
    if (window.gtag) {

      gtag('event', 'chat_feedback', {
        event_category: 'Chat',
        event_label: type,
        question: msg.question || '',
        answer: msg.text,
        feedback: type,
      });

    }

    const params = {
            feedback_type: type,
            question: msg.question,
            answer: msg.text
    }

    ActionRequest('chatBot/ActionStoreSendChatbotFeedback', params)
      .then((res) =>{
        //
      })

  }

async function sendMessage() {

  if (!newMessage.value.trim()) return

  isLoading.value = true

  const userMessage = {
    from: 'user',
    text: newMessage.value,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  }
  
  messages.value.push(userMessage)
  newMessage.value = ''

  const url = `${process.env.VUE_APP_ROOT_API}chat`

  await fetchEventSource(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      },
      body: JSON.stringify({
        messages: messages.value
      }),
      openWhenHidden: true,
      onmessage(ev) {

        // Captura a última pergunta feita pelo usuário
        const lastUserMsg = messages.value.slice().reverse().find(msg => msg.from === 'user');

        messages.value.push({
          from: 'bot',
          text: JSON.parse(ev.data),
          question: lastUserMsg ? lastUserMsg.text : '',
          feedback: null,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        })

        isLoading.value = false
      },
      onerror(err) {
        isLoading.value = false
      },
    })

}


</script>

<style scoped>
.chat-content {
  height: calc(100vh - 215px);
  overflow-y: auto;
  padding: 8px;
}

.chat-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}
.user {
  flex-direction: row-reverse;
}
.avatar {
  margin: 8px 4px;
}
.chat-message-wrapper {
  max-width: 70%;
  display: flex;
  flex-direction: column;
}
.message-bubble {
  border-radius: 12px;
  padding: 10px 14px;
  word-wrap: break-word;
  white-space: normal;
  font-size: 0.95rem;
  margin-bottom: 0 !important;
}
.bot .message-bubble {
  background-color: #d9e4ee;
  color: #000;
}
.user .message-bubble {
  background-color: #0066cc;
  color: #fff;
}
.timestamp {
  font-size: 0.75rem;
  color: #777;
  margin-top: 4px;
  padding-left: 4px;
}
.user .timestamp {
  text-align: right;
  padding-right: 4px;
}

/* Markdown base */
.message-bubble h1, .message-bubble h2 {
  font-size: 1.1rem;
  margin: 4px 0;
}
.message-bubble p {
  margin: 4px 0;
}
.message-bubble code {
  background-color: #f4f4f4;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: monospace;
}
.message-bubble pre {
  background-color: #2d2d2d;
  color: #f8f8f2;
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
}
.message-bubble pre code {
  background: none;
  color: inherit;
  padding: 0;
}

.message-bubble p:last-child {
  margin-bottom: 0;
}



</style>
